import React, { useState, useEffect } from 'react';
import { remakeLabelOf, remakeBadgeColors } from '../utils/remakeKind';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, deleteDoc, addDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import {
    Package, Search, Trash2, Plus, Send, Inbox, List, X, ChevronLeft, ChevronRight,
    Home, Edit2, FileText as FileTextIcon, Filter, ArrowDownAZ, Mail, RefreshCw
} from 'lucide-react';
import PageHeader from './common/PageHeader';
import LabelPrintModal from './LabelPrintModal';
import { buildLabelData } from '../utils/labelData';
import { markOrderReceivedStage } from '../utils/productionStage';
import { stage3Of, clinicStageOf, STAGE3_COLOR } from '../utils/shared/clinic-stage';   // 공정 3단계 — 기공소 stage3Of · 치과 clinicStageOf (2026-10-12)
import StlViewer from './StlViewer';

function OrderList({ user }) {
    const navigate = useNavigate();
    const [sentOrders, setSentOrders] = useState([]);
    const [receivedOrders, setReceivedOrders] = useState([]);
    const [outsourceOrders, setOutsourceOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [orderTypeTab, setOrderTypeTab] = useState('all');
    const [sortBy, setSortBy] = useState('latest');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actualBusinessType, setActualBusinessType] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedPartner, setSelectedPartner] = useState('all');
    const [availableMonths, setAvailableMonths] = useState([]);
    const [availablePartners, setAvailablePartners] = useState([]);
    const [imageModal, setImageModal] = useState({ open: false, images: [], currentIndex: 0 });

    // 🦷 「공정」 칸 (2026-09-02 대표 지시, 2026-09-29 4단계: 의뢰서 접수/확인/디자인완료/밀링완료 + 완료 — utils/clinicStage.js 정본, auto_due 분기 포함).
    //  ※ 2026-10-12: 기공소 화면은 공정 3단계(utils/shared/clinic-stage.js stage3Of — 의뢰서 확인/디자인 완료/포장 완료, 확인 전은 칩 없음).
    //    치과 화면(보낸 의뢰서)도 같은 3단계(clinicStageOf — 대표 10-12 "권고대로"). 확인 전·취소 = 칩 없음, auto_due = '도착 예정일 지남'.
    //  스캔 전송 상태·밀링 감지 등 세부 신호는 배지 툴팁(detail)로만.
    //  치과(보낸 의뢰서)·기공소(받은 의뢰서) 공통. scanMap[scanId] = scans 문서 | null(없음) | {status:'unknown'}(읽기 실패).
    //  목록에 보이는 건만, 한 번 읽은 건 다시 읽지 않는다.
    const isClinicView = actualBusinessType === 'dental' || actualBusinessType === 'clinic';
    const isLabView = actualBusinessType === 'lab' || user?.businessType === 'lab' || user?.businessType === '기공소' || user?.companyBusinessType === 'lab';
    const showStageCol = isClinicView || isLabView;
    const [scanMap, setScanMap] = useState({});
    useEffect(() => {
        if (!showStageCol) return;
        const ids = [...new Set(filteredOrders.map(o => o.scanId).filter(Boolean))].filter(id => !(id in scanMap)).slice(0, 80);
        if (!ids.length) return;
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(ids.map(async (id) => {
                try {
                    const snap = await getDoc(doc(db, 'scans', id));
                    return [id, snap.exists() ? snap.data() : null];
                } catch (e) {
                    console.error('공정 칸 스캔 조회 실패:', id, e?.message || e);
                    return [id, { status: 'unknown' }];   // 권한·네트워크 실패 = '연결 끊김'으로 오인하지 않는다
                }
            }));
            if (!cancelled) setScanMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
        })();
        return () => { cancelled = true; };
    }, [showStageCol, filteredOrders, scanMap]);   // scanMap 포함 — 80건 초과분 이어 읽기(ids 비면 조기 종료, 09-03 점검 #7)

    // 이메일 동기화 state
    const [emailSyncing, setEmailSyncing] = useState(false);

    // 라벨 일괄 인쇄 state
    const [labelMode, setLabelMode] = useState(false);           // 선택 모드 on/off
    const [labelSelected, setLabelSelected] = useState({});      // { orderId: true }
    const [showLabelModal, setShowLabelModal] = useState(false);
    // 디자인 3D 미리보기 (2026-09-04 대표 요청: 목록 행에서도 바로 열기) — 의뢰서 상세와 같은 뷰어·같은 entries 규약
    const [stlPreview, setStlPreview] = useState(null);
    const openDesignPreview = (order, e) => {
        e.stopPropagation();
        const files = (order.designPreview && Array.isArray(order.designPreview.files)) ? order.designPreview.files : [];
        if (!files.length) return;
        const ordered = [...files].sort((a, b) => String(a.tooth || '').localeCompare(String(b.tooth || '')));
        setStlPreview({
            entries: ordered.map(f => ({ url: f.url, path: f.path, name: (f.path || '').split('/').pop(), tooth: f.tooth, kind: f.kind, format: f.format, size: f.size, reduced: !!f.reduced, layer: f.layer || null, part: f.part || null })),
            title: `디자인 미리보기 · ${order.patientName || ''}`,
            notice: '미리보기는 디자인 확인용이며 최종 보철과 다를 수 있습니다. 완료 처리 후 자동 삭제됩니다.',
        });
    };

    // 라벨 인쇄 가능한 의뢰서인지 (메시지·외주·환자명 없는 이메일 제외)
    const isLabelable = (order) =>
        !order.isOutsource && order.type !== 'message' &&
        !(order.source === 'email' && !order.patientName);

    const toggleLabelSelect = (orderId) => {
        setLabelSelected(prev => {
            const next = { ...prev };
            if (next[orderId]) delete next[orderId];
            else next[orderId] = true;
            return next;
        });
    };

    const labelSelectedOrders = filteredOrders.filter(o => labelSelected[o.id]);

    const handleBatchLabelPrinted = () => {
        // 다른 인쇄와 동일하게 인쇄 기록 (수신 의뢰서가 대기 상태면 진행중(in_progress) 전환)
        const targetId = user?.companyId || user?.uid;
        labelSelectedOrders.forEach(order => {
            const updateData = {
                printedAt: serverTimestamp(),
                printedBy: user?.uid || null
            };
            if (targetId === order.toUserId && order.status === 'pending') {
                updateData.status = 'in_progress';
            }
            updateDoc(doc(db, 'workOrders', order.id), updateData)
                .catch(error => console.error('인쇄 기록 실패:', order.id, error));
            markOrderReceivedStage(order, user);   // 라벨 인쇄 실행 = 제작 현황 '의뢰서확인' 진입
        });
    };

    // 이메일 동기화 핸들러
    const handleEmailSync = async () => {
        const labId = user?.companyId || user?.uid;
        if (!labId) {
            alert('사용자 정보를 불러올 수 없습니다.');
            return;
        }
        if (!window.confirm('이메일에서 새 의뢰서를 가져옵니다.\n계속하시겠습니까?')) return;

        setEmailSyncing(true);
        try {
            const functions = getFunctions(undefined, 'asia-northeast3');
            const syncEmailsManual = httpsCallable(functions, 'syncEmailsManual');
            const result = await syncEmailsManual({ labId });
            const count = result?.data?.count || 0;
            alert(count > 0
                ? `✅ 동기화 완료!\n${count}개의 새 의뢰서를 가져왔습니다.`
                : '✅ 동기화 완료!\n새로운 의뢰서가 없습니다.'
            );
        } catch (error) {
            console.error('이메일 동기화 오류:', error);
            const msg = error?.message || '동기화 중 오류가 발생했습니다.';
            if (msg.includes('이메일 설정 없음') || msg.includes('failed-precondition')) {
                if (window.confirm('이메일 설정이 필요합니다.\n설정 페이지로 이동할까요?')) {
                    navigate('/email-settings');
                }
            } else {
                alert(`❌ 동기화 실패\n${msg}`);
            }
        } finally {
            setEmailSyncing(false);
        }
    };

    const getPartnerName = (order, isSent) => {
        if (order.isOutsource) {
            return order.isOutsourceSent ? (order.toUserName || '업체명 없음') : (order.fromUserName || '업체명 없음');
        }
        if (order.isInternalLab) {
            return order.internalLabName || "내 기공실";
        }
        if (isSent) {
            return order.toCompanyName || order.labName || order.toUserName || order.receiverName || '업체명 없음';
        } else {
            return order.fromCompanyName || order.dentistName || order.fromUserName || order.senderName || '업체명 없음';
        }
    };

    const getOutsourceTypeLabel = (type) => {
        const map = { design: '디자인', milling: '밀링', 'design+milling': '디자인+밀링' };
        return map[type] || type;
    };

    const getDeliveryDate = (order) => {
        const dateValue = order.deliveryDate || order.dueDate || order.completionDate || order.deliveryDueDate;
        if (!dateValue) return null;
        if (dateValue.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
        if (dateValue instanceof Date) return dateValue;
        if (typeof dateValue === 'string') return new Date(dateValue);
        return null;
    };

    useEffect(() => {
        if (!user) {
            setLoading(false);
            setError('사용자 정보가 없습니다.');
            return;
        }

        let cancelled = false;
        let unsubscribeSent = null;
        let unsubscribeReceived = null;
        let unsubscribeOutsource = null;

        const setupOrderListeners = async () => {
            try {
                setLoading(true);
                setError(null);

                const targetId = user.companyId || user.uid;
                let businessType = null;

                if (user.userType === 'staff' || user.businessType === 'staff') {
                    if (!user.companyId) {
                        setError('소속 회사 정보가 없습니다.');
                        setLoading(false);
                        return;
                    }
                    const companyDoc = await getDoc(doc(db, 'users', user.companyId));
                    if (cancelled) return;
                    if (companyDoc.exists()) {
                        const companyData = companyDoc.data();
                        businessType = companyData.businessType || companyData.companyBusinessType;
                    } else {
                        setError('회사 정보를 찾을 수 없습니다.');
                        setLoading(false);
                        return;
                    }
                } else {
                    businessType = user.businessType || user.companyBusinessType;
                }

                if (cancelled) return;

                if (!businessType || (businessType !== 'dental' && businessType !== 'clinic' && businessType !== 'lab')) {
                    setError('사업자 유형이 올바르지 않습니다.');
                    setLoading(false);
                    return;
                }

                setActualBusinessType(businessType);

                const sentQuery = query(
                    collection(db, 'workOrders'),
                    where('fromUserId', '==', targetId),
                    orderBy('createdAt', 'desc')
                );

                if (cancelled) return;
                unsubscribeSent = onSnapshot(sentQuery, (snapshot) => {
                    if (cancelled) return;
                    const ordersList = snapshot.docs.filter(d => d.data().hiddenForSender !== true).map(docSnapshot => {
                        const data = docSnapshot.data();
                        return {
                            id: docSnapshot.id,
                            ...data,
                            orderType: 'sent',
                            orderDate: data.orderDate?.toDate() || data.createdAt?.toDate() || new Date()
                        };
                    }).filter(o => o.source !== 'agent_auto'); // 에이전트 수집 의뢰서는 거래명세서에서만 사용
                    setSentOrders(ordersList);
                    setLoading(false);
                }, (error) => {
                    console.error('보낸주문 스트림 오류:', error);
                    setLoading(false);
                });

                const receivedQuery = query(
                    collection(db, 'workOrders'),
                    where('toUserId', '==', targetId),
                    orderBy('createdAt', 'desc')
                );

                if (cancelled) return;
                unsubscribeReceived = onSnapshot(receivedQuery, (snapshot) => {
                    if (cancelled) return;
                    const ordersList = snapshot.docs.map(docSnapshot => {
                        const data = docSnapshot.data();
                        return {
                            id: docSnapshot.id,
                            ...data,
                            orderType: 'received',
                            orderDate: data.orderDate?.toDate() || data.createdAt?.toDate() || new Date()
                        };
                    }).filter(o => o.source !== 'agent_auto'); // 에이전트 수집 의뢰서는 거래명세서에서만 사용
                    setReceivedOrders(ordersList);
                    setLoading(false);
                }, (error) => {
                    console.error('받은주문 스트림 오류:', error);
                    setLoading(false);
                });

            } catch (error) {
                if (!cancelled) {
                    setError('주문 목록 초기화에 실패했습니다: ' + error.message);
                    setLoading(false);
                }
            }
        };

        setupOrderListeners();

        return () => {
            cancelled = true;
            if (unsubscribeSent) unsubscribeSent();
            if (unsubscribeReceived) unsubscribeReceived();
            // unsubscribeOutsource removed
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid, user?.companyId, user?.userType, user?.businessType]);

    useEffect(() => {
        const filteredReceivedOrders = receivedOrders.filter(order => !order.isInternalLab);

        let allOrders = [];
        if (orderTypeTab === 'all') {
            allOrders = [...sentOrders, ...filteredReceivedOrders];
        } else if (orderTypeTab === 'sent') {
            allOrders = [...sentOrders];
        } else if (orderTypeTab === 'received') {
            allOrders = [...filteredReceivedOrders];
        } else if (orderTypeTab === 'internal') {
            allOrders = sentOrders.filter(order => order.isInternalLab);
        }

        const monthSet = new Set();
        const partnerSet = new Set();
        allOrders.forEach(order => {
            const orderDate = order.orderDate || new Date();
            const monthStr = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
            monthSet.add(monthStr);
            const partnerName = getPartnerName(order, order.orderType === 'sent');
            if (partnerName && partnerName !== '업체명 없음') {
                partnerSet.add(partnerName);
            }
        });
        setAvailableMonths(['all', ...Array.from(monthSet).sort().reverse()]);
        setAvailablePartners(['all', ...Array.from(partnerSet).sort()]);

        let filtered = [...allOrders];

        if (searchQuery) {
            filtered = filtered.filter(order => {
                const searchLower = searchQuery.toLowerCase();
                return (
                    order.patientName?.toLowerCase().includes(searchLower) ||
                    order.emailSubject?.toLowerCase().includes(searchLower) || // 메시지 제목 검색 추가
                    getPartnerName(order, order.orderType === 'sent').toLowerCase().includes(searchLower)
                );
            });
        }


        if (selectedPartner !== 'all') {
            filtered = filtered.filter(order => {
                const partnerName = getPartnerName(order, order.orderType === 'sent');
                return partnerName === selectedPartner;
            });
        }

        if (selectedMonth !== 'all') {
            const [year, month] = selectedMonth.split('-').map(Number);
            filtered = filtered.filter(order => {
                const orderDate = order.orderDate || new Date();
                return orderDate.getFullYear() === year && orderDate.getMonth() === month - 1;
            });
        }

        filtered.sort((a, b) => {
            if (sortBy === 'latest') return b.orderDate - a.orderDate;
            else if (sortBy === 'oldest') return a.orderDate - b.orderDate;
            else if (sortBy === 'partner') {
                const aName = getPartnerName(a, a.orderType === 'sent');
                const bName = getPartnerName(b, b.orderType === 'sent');
                return aName.localeCompare(bName);
            }
            return 0;
        });

        setFilteredOrders(filtered);
        // eslint-disable-next-line react-hooks/exhaustive-deps    
    }, [searchQuery, sortBy, sentOrders, receivedOrders, orderTypeTab, selectedMonth, selectedPartner]);

    // [CAD로 보내기] 제거 (2026-10-25 대표) — 상태·PC 선택·agentCommands(open_in_cad) 전송 코드 삭제. 복원 근거: docs/조사_CAD로보내기_제거범위_2026-10-25.md

    const sendOrderToOutsource = (order, e) => {
        e.stopPropagation();
        const items = (order.items || []).filter(i => i.toothNumber != null && i.toothNumber !== '');
        const toYmd = (t) => {
            const d = t?.toDate ? t.toDate() : (t instanceof Date ? t : (t ? new Date(t) : null));
            if (!d || isNaN(d)) return '';
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        navigate('/outsource-request', {
            state: {
                compose: true,
                prefill: {
                    patientName: order.patientName || '',
                    teethDetails: items.map(i => ({
                        tooth: i.toothNumber, prosthesis: i.prosthesis || '', material: i.material || '',
                        shade: i.shade || '', implant: i.implant || '', isPontic: !!i.isPontic, isBridge: !!i.isBridge,
                    })),
                    dueDate: toYmd(order.dueDate),
                    sourceRef: { sourceType: 'order', orderId: order.id },
                    sourceLabel: `받은 의뢰서 ${order.orderNumber || ''}`.trim(),
                    sourceNotes: order.specialNotes || '',
                },
            },
        });
    };

    // 실제 전송 — targetDevice={deviceId,deviceName}|null, cadReady=지금 처리 가능한 PC가 있는지
    const handleDeleteOrder = async (orderId, e, isOutsource = false, isSentOrder = false, isExternal = false) => {
        e.stopPropagation();
        // 발신자(치과) 삭제 = 내 목록에서만 숨김 — 상대 기공소의 받은 의뢰서에는 그대로 남음 (2026-07-25 사장 확정)
        // 직접 기록(수기 행)은 수신자가 없어 같은 숨김 방식으로 지운다 (rules delete는 수신자 전용이라 실삭제 불가)
        if (isSentOrder && !isOutsource) {
            const msg = isExternal
                ? '이 직접 기록을 삭제하시겠습니까?'
                : '이 의뢰서를 내 목록에서 삭제하시겠습니까?\n(상대 기공소의 받은 의뢰서에는 그대로 남습니다)';
            if (!window.confirm(msg)) return;
            try {
                await updateDoc(doc(db, 'workOrders', orderId), { hiddenForSender: true, updatedAt: new Date() });
            } catch (error) {
                alert('삭제에 실패했습니다: ' + error.message);
            }
            return;
        }
        if (window.confirm('정말로 이 주문을 삭제하시겠습니까?')) {
            try {
                const collectionName = isOutsource ? 'outsourceOrders' : 'workOrders';
                await deleteDoc(doc(db, collectionName, orderId));
            } catch (error) {
                alert('삭제에 실패했습니다: ' + error.message);
            }
        }
    };

    // (구) 치과 도착 확인 버튼(handleConfirmReceipt, 2026-08-25 P1) — 2026-09-29 무접촉 공정 흐름(버튼 0개)으로 제거.
    //  완료는 서버가 납기일 자정에 자동 처리(stageSource 'auto_due'). 과거 수동 기록(receivedAt)은 읽기만 한다.
    const arrivalNoteOf = (order) => {
        if (order.stageSource === 'auto_due') return '도착 예정일 지남(자동)';
        if (order.receivedAt) return '도착 확인됨(수동)';
        return null;
    };

    // '연결 전' 수기 행 (2026-08-25 에이전트 화면 기준안 P2, 대표 승인) — 덴클 안 쓰는 기공소 건을
    // 치과가 한 줄로 직접 기록해 대장을 완결한다. toUserId 없음·isExternal:true·source:'external'
    // → 공정 자동판정·멈춤/내원 알림(dailyStallDigest) 대상에서 제외.
    const emptyExtForm = { patientName: '', labName: '', prosthesis: '', dueDate: '', visitDate: '', stage: '' };
    const [showExternalModal, setShowExternalModal] = useState(false);
    const [extForm, setExtForm] = useState(emptyExtForm);
    const [extSaving, setExtSaving] = useState(false);

    const handleCreateExternal = async () => {
        const patientName = extForm.patientName.trim();
        const labName = extForm.labName.trim();
        if (!patientName || !labName) {
            alert('환자명과 기공소명을 입력해 주세요.');
            return;
        }
        setExtSaving(true);
        try {
            const targetId = user.companyId || user.uid;
            await addDoc(collection(db, 'workOrders'), {
                fromUserId: targetId,
                fromUserName: user.businessName || user.companyName || user.name || '',
                patientName,
                isExternal: true,
                source: 'external',
                externalLabName: labName,
                labName,   // getPartnerName(발신) 거래처 표시용
                ...(extForm.prosthesis.trim() ? { prosthesisSummary: extForm.prosthesis.trim() } : {}),
                ...(extForm.dueDate ? { dueDate: new Date(extForm.dueDate + 'T00:00:00') } : {}),
                ...(extForm.visitDate ? { visitDate: new Date(extForm.visitDate + 'T00:00:00') } : {}),
                // 공정은 수동 지정만 — 미지정이면 processStage 자체를 넣지 않는다 (자동판정 없음)
                ...(extForm.stage ? { processStage: extForm.stage, stageUpdatedAt: serverTimestamp(), stageSource: 'manual' } : {}),
                status: 'in_progress',
                readByReceiver: true,   // 수신자 없음 — 미읽음 강조 방지
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setShowExternalModal(false);
            setExtForm(emptyExtForm);
        } catch (err) {
            console.error('직접 기록 저장 실패:', err);
            alert('직접 기록 저장에 실패했습니다: ' + (err?.message || '잠시 후 다시 시도해 주세요.'));
        } finally {
            setExtSaving(false);
        }
    };

    const openImageModal = (images, index, e) => {
        e.stopPropagation();
        setImageModal({ open: true, images, currentIndex: index });
    };

    const closeImageModal = () => {
        setImageModal({ open: false, images: [], currentIndex: 0 });
    };

    const nextImage = (e) => {
        e.stopPropagation();
        setImageModal(prev => ({
            ...prev,
            currentIndex: (prev.currentIndex + 1) % prev.images.length
        }));
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setImageModal(prev => ({
            ...prev,
            currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
        }));
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return date.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
    };

    // PDF/인쇄 상태 텍스트 렌더링
    const renderPrintStatus = (order) => {
        const statuses = [];
        if (order.downloadedPdfAt) {
            statuses.push(<span key="pdf" style={styles.statusPdf}>PDF</span>);
        }
        if (order.printedAt) {
            statuses.push(<span key="print" style={styles.statusPrint}>인쇄</span>);
        }
        if (statuses.length === 0) {
            return <span style={styles.noStatus}>-</span>;
        }
        return <div style={styles.statusContainer}>{statuses}</div>;
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p style={styles.loadingText}>주문 목록을 불러오는 중...</p>
                </div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <Package size={64} color="#ef4444" />
                    <p style={styles.errorText}>{error}</p>
                    <button style={styles.retryButton} onClick={() => window.location.reload()}>새로고침</button>
                </div>
            </div>
        );
    }

    const totalCount = sentOrders.length + receivedOrders.filter(o => !o.isInternalLab).length;

    return (
        <div style={styles.container}>
            <PageHeader icon={FileTextIcon} title="주문목록" />
            <div style={styles.scrollView}>
                <div style={styles.contentWrapper}>

                    {/* 필터 카드 */}
                    <div style={styles.filterCard}>
                        {/* Row 1: 구분 */}
                        <div style={styles.filterRow}>
                            <div style={styles.filterLabel}><List size={14} color="#6366f1" /><span>구분</span></div>
                            <div style={styles.tabGroup}>
                                {[
                                    { key: 'all', label: `전체 ${totalCount}`, icon: <List size={12} /> },
                                    { key: 'sent', label: `발신 ${sentOrders.length}`, icon: <Send size={12} /> },
                                    { key: 'received', label: `수신 ${receivedOrders.filter(o => !o.isInternalLab).length}`, icon: <Inbox size={12} /> },
                                    { key: 'internal', label: `내 기공실 ${sentOrders.filter(o => o.isInternalLab).length}`, icon: <Home size={12} /> },
                                ].map(t => (
                                    <button key={t.key} onClick={() => setOrderTypeTab(t.key)}
                                        style={{ ...styles.tabBtn, ...(orderTypeTab === t.key ? styles.tabBtnActive : {}) }}>
                                        {t.icon}{t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Row 2: 정렬 + 필터 */}
                        <div style={styles.filterRow}>
                            <div style={styles.filterLabel}><ArrowDownAZ size={14} color="#6366f1" /><span>정렬</span></div>
                            <div style={styles.tabGroup}>
                                {[
                                    { key: 'latest', label: '최신순' },
                                    { key: 'oldest', label: '오래된순' },
                                    { key: 'partner', label: '거래처순' },
                                ].map(t => (
                                    <button key={t.key} onClick={() => setSortBy(t.key)}
                                        style={{ ...styles.tabBtn, ...(sortBy === t.key ? styles.tabBtnActive : {}) }}>
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 8px' }} />
                            <div style={styles.filterLabel}><Filter size={14} color="#6366f1" /><span>필터</span></div>
                            <select value={selectedPartner} onChange={(e) => setSelectedPartner(e.target.value)} style={styles.filterSelect}>
                                {availablePartners.map(partner => (
                                    <option key={partner} value={partner}>
                                        {partner === 'all' ? '전체 거래처' : partner}
                                    </option>
                                ))}
                            </select>
                            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={styles.filterSelect}>
                                {availableMonths.map(month => (
                                    <option key={month} value={month}>
                                        {month === 'all' ? '전체월' : `${month.split('-')[1]}월`}
                                    </option>
                                ))}
                            </select>
                            {(selectedMonth !== 'all' || selectedPartner !== 'all') && (
                                <button style={styles.resetBtn} onClick={() => { setSelectedMonth('all'); setSelectedPartner('all'); }}>
                                    초기화
                                </button>
                            )}
                        </div>

                        {/* Row 3: 검색 */}
                        <div style={styles.filterRow}>
                            <div style={styles.filterLabel}><Search size={14} color="#6366f1" /><span>검색</span></div>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <input
                                    type="text"
                                    placeholder="환자명/거래처 검색"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={styles.filterInput}
                                />
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery('')}
                                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                                        <X size={14} color="#94a3b8" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 액션 버튼 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* 제한 모드(결제 전 기공소)는 주문 작성 경로가 잠겨 있어 숨김 (W5) — 치과는 의뢰서 작성 무료(대표 10-15) */}
                            {user?.accessMode !== 'lab_limited' && (<>
                            <button style={styles.newOrderBtn} onClick={() => navigate('/create-order')}>
                                <Plus size={16} /><span>새 주문</span>
                            </button>
                            <button style={{ ...styles.newOrderBtn, backgroundColor: '#ffffff', color: '#6366f1', border: '1px solid #c7d2fe' }} onClick={() => navigate('/handwritten-order')}>
                                <span>✍️</span><span>필기 주문</span>
                            </button>
                            </>)}
                            {/* '연결 전' 수기 행 (2026-08-25 P2) — 치과 계정만: 덴클 안 쓰는 기공소 건 한 줄 기록 */}
                            {(actualBusinessType === 'dental' || actualBusinessType === 'clinic') && (
                                <button
                                    style={{ ...styles.newOrderBtn, backgroundColor: '#ffffff', color: '#0f766e', border: '1px solid #99f6e4' }}
                                    onClick={() => setShowExternalModal(true)}
                                    title="덴클을 아직 안 쓰는 기공소에 맡긴 건도 한 줄로 기록해 대장을 완성합니다"
                                >
                                    <Plus size={16} /><span>직접 기록</span>
                                </button>
                            )}
                            {/* 이메일 동기화 버튼 (기공소 전용) */}
                            {(actualBusinessType === 'lab' || user?.businessType === 'lab' || user?.businessType === '기공소' || user?.companyBusinessType === 'lab') && (
                                <button
                                    onClick={handleEmailSync}
                                    disabled={emailSyncing}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '8px 14px',
                                        backgroundColor: emailSyncing ? '#e2e8f0' : '#fff',
                                        color: emailSyncing ? '#94a3b8' : '#0369a1',
                                        border: '1px solid #bae6fd',
                                        borderRadius: 8,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: emailSyncing ? 'wait' : 'pointer',
                                        transition: 'all 0.15s',
                                        fontFamily: 'inherit',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!emailSyncing) {
                                            e.currentTarget.style.backgroundColor = '#f0f9ff';
                                            e.currentTarget.style.borderColor = '#7dd3fc';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!emailSyncing) {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.borderColor = '#bae6fd';
                                        }
                                    }}
                                    title="이메일에서 새 의뢰서 가져오기"
                                >
                                    {emailSyncing ? (
                                        <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                                    ) : (
                                        <Mail size={15} />
                                    )}
                                    <span>{emailSyncing ? '가져오는 중...' : '이메일에서 가져오기'}</span>
                                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                                </button>
                            )}
                            {/* 라벨 일괄 인쇄 */}
                            {!labelMode ? (
                                <button
                                    onClick={() => { setLabelMode(true); setLabelSelected({}); }}
                                    style={{ ...styles.newOrderBtn, backgroundColor: '#fff', color: '#b45309', border: '1px solid #fcd34d' }}
                                    title="여러 의뢰서를 골라 라벨지에 한 번에 인쇄합니다"
                                >
                                    <span>🏷️</span><span>라벨 인쇄</span>
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => {
                                            if (labelSelectedOrders.length === 0) { alert('인쇄할 의뢰서를 목록에서 체크해 주세요.'); return; }
                                            setShowLabelModal(true);
                                        }}
                                        style={{ ...styles.newOrderBtn, backgroundColor: '#f59e0b' }}
                                    >
                                        <span>🏷️</span><span>선택한 {labelSelectedOrders.length}건 라벨 인쇄</span>
                                    </button>
                                    <button
                                        onClick={() => { setLabelMode(false); setLabelSelected({}); }}
                                        style={{ ...styles.newOrderBtn, backgroundColor: '#fff', color: '#64748b', border: '1px solid #cbd5e1' }}
                                    >
                                        <span>취소</span>
                                    </button>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>목록에서 환자를 체크하세요</span>
                                </>
                            )}
                        </div>
                        {user.userType !== 'staff' && (
                            <button style={{ ...styles.editModeBtn, ...(isEditMode ? styles.editModeBtnActive : {}) }} onClick={() => setIsEditMode(!isEditMode)}>
                                <Edit2 size={16} /><span>{isEditMode ? "완료" : "편집"}</span>
                            </button>
                        )}
                    </div>

                    {/* 테이블 */}
                    {filteredOrders.length > 0 ? (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <colgroup>
                                    {labelMode && <col style={{ width: '4%' }} />}{/* 선택 */}
                                    {/* ⚠️ 열을 추가하면 여기도 같이 — tableLayout:fixed라 colgroup에 없는 마지막 열은 너비 0으로 가려진다(09-02 사고) */}
                                    <col style={{ width: '9%' }} />{/* 구분 */}
                                    <col style={{ width: '12%' }} />{/* 환자 */}
                                    <col style={{ width: showStageCol ? '14%' : '16%' }} />{/* 거래처 */}
                                    <col style={{ width: '11%' }} />{/* 주문일 */}
                                    <col style={{ width: '11%' }} />{/* 납기일 */}
                                    {showStageCol && <col style={{ width: '12%' }} />}{/* 공정 */}
                                    <col style={{ width: showStageCol ? '8%' : '12%' }} />{/* 이미지 */}
                                    <col style={{ width: '8%' }} />{/* 출력 */}
                                    <col style={{ width: showStageCol ? '15%' : '16%' }} />{/* 삭제·CAD */}
                                </colgroup>
                                <thead>
                                    <tr>
                                        {labelMode && (
                                            <th style={styles.th}>
                                                <input
                                                    type="checkbox"
                                                    checked={filteredOrders.filter(isLabelable).length > 0 && filteredOrders.filter(isLabelable).every(o => labelSelected[o.id])}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const next = {};
                                                            filteredOrders.filter(isLabelable).forEach(o => next[o.id] = true);
                                                            setLabelSelected(next);
                                                        } else {
                                                            setLabelSelected({});
                                                        }
                                                    }}
                                                    title="전체 선택"
                                                />
                                            </th>
                                        )}
                                        <th style={styles.th}>구분</th>
                                        <th style={styles.th}>환자</th>
                                        <th style={styles.th}>거래처</th>
                                        <th style={styles.th}>주문일</th>
                                        <th style={styles.th}>납기일</th>
                                        {showStageCol && <th style={styles.th}>공정</th>}
                                        <th style={styles.th}>이미지</th>
                                        <th style={styles.th}>출력</th>
                                        <th style={styles.th}>삭제</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map((order, index) => {
                                        const isOutsource = order.isOutsource === true;
                                        const isSent = isOutsource ? order.isOutsourceSent : (order.orderType === 'sent');
                                        const partnerName = getPartnerName(order, isSent);
                                        const deliveryDate = isOutsource ? null : getDeliveryDate(order);
                                        const hasImages = !isOutsource && order.imageUrls && order.imageUrls.length > 0;
                                        const isUnread = !order.readByReceiver;
                                        // 도착 처리 표기 (2026-09-29): 내가 보낸 웹 의뢰서 중 자동 완료(auto_due) 또는 과거 수동 기록(receivedAt)만 작은 글씨로.
                                        //  기공소 계정·외주·내부·메시지는 제외. 버튼 없음.
                                        const arrivalNote = (isSent && !isOutsource && !order.isInternalLab &&
                                            order.type !== 'message' && actualBusinessType !== 'lab') ? arrivalNoteOf(order) : null;

                                        return (
                                            <tr
                                                key={order.id}
                                                style={{ ...styles.tr, ...(index % 2 === 1 ? { background: '#f8fafc' } : {}) }}
                                                className={isUnread ? 'unread-row' : ''}
                                                onClick={() => {
  // 라벨 선택 모드: 행 클릭 = 체크 토글
  if (labelMode) {
    if (isLabelable(order)) toggleLabelSelect(order.id);
    return;
  }

  if (isOutsource) {
    navigate('/outsource-request');   // 10-30: 옛 외주 화면 삭제 — 옛 건은 외주 의뢰서 › 보낸 의뢰 › 이전 외주(읽기 전용)
    return;
  }

  // 직접 기록(수기 행)은 상세 화면(의뢰서 양식)이 없다 — 이동하지 않음 (2026-08-25 P2)
  if (order.isExternal) return;

  // type이 'message'이거나, 이메일인데 patientName이 없으면 메시지함으로 이동
  if (order.type === 'message' ||
      (order.source === 'email' && !order.patientName)) {
    navigate(`/view-message/${order.id}`);
  } else {
    navigate(`/view-order/${order.id}`);
  }
}}
                                            >
                                                {labelMode && (
                                                    <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                                        {isLabelable(order) ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={!!labelSelected[order.id]}
                                                                onChange={() => toggleLabelSelect(order.id)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        ) : (
                                                            <span style={styles.noImage}>-</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td style={styles.td}>
                                                    <span style={{
                                                        ...styles.badge,
                                                        backgroundColor: order.isExternal ? '#f1f5f9' : isOutsource ? '#f3e8ff' : (order.type === 'message' ? '#f3e8ff' : (order.isInternalLab ? '#fef3c7' : (isSent ? '#dbeafe' : '#dcfce7'))),
                                                        color: order.isExternal ? '#475569' : isOutsource ? '#7c3aed' : (order.type === 'message' ? '#6b21a8' : (order.isInternalLab ? '#92400e' : (isSent ? '#1e40af' : '#166534')))
                                                    }}>
                                                        {order.isExternal ? '직접 입력' : isOutsource ? (order.isOutsourceSent ? '외주발신' : '외주수신') : (order.type === 'message' ? '💬 메시지' : ['dentweb', 'email', 'r2cad'].includes(order.source) ? '📧 이메일' : order.isInternalLab ? '🏠 내부' : (isSent ? '발신' : '수신'))}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {order.orderType === 'handwritten' && order.handwrittenImageUrl && (
                                                            <img
                                                                src={order.handwrittenImageUrl}
                                                                alt="필기 의뢰서"
                                                                style={{ width: 30, height: 42, objectFit: 'cover', objectPosition: 'top', borderRadius: 4, border: '1px solid #e2e8f0', flexShrink: 0 }}
                                                            />
                                                        )}
                                                        <span style={styles.patientName}>
                                                            {order.type === 'message' ? '-' : (order.patientName || '-')}
                                                        </span>
                                                        {order.orderType === 'handwritten' && (
                                                            <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: '4px', fontWeight: '600' }}>✍️ 필기</span>
                                                        )}
                                                        {/* 재제작/수리/수정 배지 — remakeKind 분기 (2026-09-04), 구데이터(remakeKind 없음)=리메이크 */}
                                                        {order.isRemake && (() => { const c = remakeBadgeColors(order); return (
                                                            <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: c.bg, color: c.fg, borderRadius: '4px', fontWeight: '600' }}>{remakeLabelOf(order, '재제작')}</span>
                                                        ); })()}
                                                        {order.isEdited && (
                                                            <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: '#fff7ed', color: '#ea580c', borderRadius: '4px', fontWeight: '600' }}>수정됨</span>
                                                        )}
                                                        {/* 직접 기록 행: 입력한 보철 종류를 환자명 옆에 표시 (전용 열 없음 — 2026-08-25 P2) */}
                                                        {order.isExternal && order.prosthesisSummary && (
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>{order.prosthesisSummary}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.partnerText}>{partnerName}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.dateText}>{formatDate(order.orderDate)}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    {isOutsource ? (
                                                        <span style={styles.dueDateText}>
                                                            {(() => {
                                                                if (order.status === 'completed') {
                                                                    const d = order.updatedAt?.toDate ? order.updatedAt.toDate() : (order.updatedAt instanceof Date ? order.updatedAt : null);
                                                                    return d ? `${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.` : '-';
                                                                }
                                                                if (order.dueDate) {
                                                                    const d = order.dueDate?.toDate ? order.dueDate.toDate() : (order.dueDate instanceof Date ? order.dueDate : new Date(order.dueDate));
                                                                    return `${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
                                                                }
                                                                return '-';
                                                            })()}
                                                        </span>
                                                    ) : (
                                                        <span style={styles.dueDateText}>
                                                            {order.type === 'message' ? '-' : formatDate(deliveryDate)}
                                                        </span>
                                                    )}
                                                </td>
                                                {showStageCol && (
                                                    <td style={styles.td}>
                                                        {(isOutsource || order.type === 'message') ? (
                                                            <span style={{ color: '#cbd5e1' }}>-</span>
                                                        ) : (() => {
                                                            const has3d = !!(order.designPreview && order.designPreview.files && order.designPreview.files.length);
                                                            // 기공소 화면(2026-10-12 3단계): 의뢰서 확인 · 디자인 완료 · 포장 완료만. 확인 전(접수)·취소 = 칩 없음(stage3Of key null).
                                                            //   납기 자정 서버 자동 완료(auto_due)는 남은 단계 칩 + 툴팁으로만 알린다(도착·완료 단정 없음).
                                                            if (isLabView && !isClinicView) {
                                                                const s3 = stage3Of(order);
                                                                const fg3 = s3.key ? STAGE3_COLOR[s3.key] : '#94a3b8';
                                                                const tip = s3.autoDue ? '납기일 자정 자동 처리 — 납품·수령을 증명하지 않습니다' : (s3.key ? s3.label : '아직 열어 보지 않은 의뢰서');
                                                                return (
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                                        {s3.key ? (
                                                                            <span title={tip} style={{ fontSize: 12, fontWeight: 700, borderRadius: 7, padding: '5px 10px', whiteSpace: 'nowrap', display: 'inline-block', background: fg3 + '14', color: fg3 }}>
                                                                                {s3.label}
                                                                            </span>
                                                                        ) : (
                                                                            <span title={tip} style={{ color: '#cbd5e1' }}>-</span>
                                                                        )}
                                                                        {has3d && (
                                                                            <button onClick={(e) => openDesignPreview(order, e)} title="디자인 3D 미리보기"
                                                                                style={{ fontSize: 11, fontWeight: 800, padding: '3px 7px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', cursor: 'pointer', fontFamily: 'inherit' }}>3D</button>
                                                                        )}
                                                                    </span>
                                                                );
                                                            }
                                                            // 치과 화면(2026-10-12 3단계): 의뢰서 확인 · 디자인 완료 · 포장 완료. 확인 전·취소 = 칩 없음('-', 스캔 상태는 툴팁).
                                                            //   auto_due(포장 체크 없음) = '도착 예정일 지남' 회색 — 도착·받음 단정 없음. 포장 완료 툴팁 = '기공소가 포장을 마침'.
                                                            const st = clinicStageOf(order, order.scanId ? scanMap[order.scanId] : null);
                                                            const fg = st.autoDue ? '#94a3b8' : (st.key ? STAGE3_COLOR[st.key] : '#94a3b8');
                                                            const tipC = st.label ? (st.detail ? `${st.label} · ${st.detail}` : st.label) : (st.detail || '기공소 확인 전');
                                                            return (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                                    {st.label ? (
                                                                        <span title={tipC} style={{ fontSize: 12, fontWeight: 700, borderRadius: 7, padding: '5px 10px', whiteSpace: 'nowrap', display: 'inline-block', background: fg + '14', color: fg }}>
                                                                            {st.label}
                                                                        </span>
                                                                    ) : (
                                                                        <span title={tipC} style={{ color: '#cbd5e1' }}>-</span>
                                                                    )}
                                                                    {has3d && (
                                                                        <button onClick={(e) => openDesignPreview(order, e)} title="디자인 3D 미리보기"
                                                                            style={{ fontSize: 11, fontWeight: 800, padding: '3px 7px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', cursor: 'pointer', fontFamily: 'inherit' }}>3D</button>
                                                                    )}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                )}
                                                <td style={styles.td}>
                                                    {hasImages ? (
                                                        <div style={styles.imagePreviewCell}>
                                                            <img
                                                                src={order.imageUrls[0]}
                                                                alt="미리보기"
                                                                style={styles.thumbImage}
                                                                onClick={(e) => openImageModal(order.imageUrls, 0, e)}
                                                            />
                                                            {order.imageUrls.length > 1 && (
                                                                <span style={styles.imageCount}>+{order.imageUrls.length - 1}</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={styles.noImage}>-</span>
                                                    )}
                                                </td>
                                                <td style={styles.td}>
                                                    {renderPrintStatus(order)}
                                                </td>
                                                <td style={styles.td}>
                                                    {isEditMode && (order.isInternalLab || !isSent || (isSent && !isOutsource)) && user.userType !== 'staff' && (
                                                        <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={(e) => handleDeleteOrder(order.id, e, isOutsource, isSent, order.isExternal === true)}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                    {/* 도착 처리 표기 (2026-09-29 — 버튼 없음): 자동 완료는 '도착 예정일 지남(자동)', 과거 수동 기록은 '도착 확인됨(수동)' */}
                                                    {!isEditMode && arrivalNote && (
                                                        <span title="자동 처리는 편의 표시이며 납품·수령을 증명하지 않습니다. 확정은 거래명세서 확인에 따릅니다."
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 6, color: '#64748b', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                            {arrivalNote}
                                                        </span>
                                                    )}
                                                    {/* [CAD로 보내기] 버튼 제거 (2026-10-25 대표) */}
                                                    {!arrivalNote && (!isEditMode || ((!order.isInternalLab && (isOutsource ? isSent : isSent)) || user.userType === 'staff')) && (
                                                        <span style={styles.noImage}>-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={styles.emptyState}>
                            <Package size={48} color="#cbd5e1" />
                            <p style={styles.emptyText}>주문이 없습니다</p>
                            {user?.accessMode !== 'lab_limited' && (
                            <button style={styles.emptyCreateBtn} onClick={() => navigate('/create-order')}>
                                <Plus size={16} /> 새 주문
                            </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* '연결 전' 수기 행 입력 모달 (2026-08-25 P2) */}
            {showExternalModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !extSaving && setShowExternalModal(false)}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 380, maxWidth: '90vw', boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>직접 기록</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
                            {'덴클을 아직 안 쓰는 기공소에 맡긴 건입니다. 진행 상태는 자동으로 바뀌지 않고, 직접 고른 값만 표시됩니다.'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                            {[
                                { key: 'patientName', label: '환자명 *', type: 'text', placeholder: '예: 김철수' },
                                { key: 'labName', label: '기공소명 *', type: 'text', placeholder: '예: ○○기공소' },
                                { key: 'prosthesis', label: '보철 (선택)', type: 'text', placeholder: '예: 지르코니아 크라운 #36' },
                                { key: 'dueDate', label: '납기일 (선택)', type: 'date' },
                                { key: 'visitDate', label: '환자 내원일 (선택)', type: 'date' },
                            ].map(f => (
                                <div key={f.key}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{f.label}</div>
                                    <input
                                        type={f.type}
                                        value={extForm[f.key]}
                                        placeholder={f.placeholder || ''}
                                        onChange={(e) => setExtForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                    />
                                </div>
                            ))}
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{'진행 상태 (선택)'}</div>
                                <select
                                    value={extForm.stage}
                                    onChange={(e) => setExtForm(prev => ({ ...prev, stage: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', boxSizing: 'border-box', background: '#fff' }}
                                >
                                    <option value="">{'미지정'}</option>
                                    <option value="received">{'맡김'}</option>
                                    <option value="designing">{'제작 중'}</option>
                                    <option value="shipping">{'발송됨'}</option>
                                    <option value="done">{'완료'}</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button disabled={extSaving} onClick={() => setShowExternalModal(false)} style={{ padding: '8px 14px', border: '1px solid #E2E8F0', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#475569' }}>{'취소'}</button>
                            <button disabled={extSaving} onClick={handleCreateExternal} style={{ padding: '8px 16px', border: 'none', background: extSaving ? '#94a3b8' : '#0f766e', borderRadius: 8, cursor: extSaving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>{extSaving ? '저장 중…' : '기록 저장'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 라벨 일괄 인쇄 모달 */}
            {stlPreview && (
                <StlViewer entries={stlPreview.entries} title={stlPreview.title} notice={stlPreview.notice} onClose={() => setStlPreview(null)} />
            )}
            {showLabelModal && labelSelectedOrders.length > 0 && (
                <LabelPrintModal
                    items={labelSelectedOrders.map(order =>
                        buildLabelData(order, getPartnerName(order, order.orderType === 'sent'))
                    )}
                    onClose={() => setShowLabelModal(false)}
                    onPrinted={handleBatchLabelPrinted}
                />
            )}

            {/* 이미지 모달 */}
            {imageModal.open && (
                <div style={styles.imageModal} onClick={closeImageModal}>
                    <button style={styles.modalCloseBtn} onClick={closeImageModal}>
                        <X size={28} color="#fff" />
                    </button>
                    {imageModal.images.length > 1 && (
                        <>
                            <button style={{ ...styles.modalNavBtn, left: 20 }} onClick={prevImage}>
                                <ChevronLeft size={32} color="#fff" />
                            </button>
                            <button style={{ ...styles.modalNavBtn, right: 20 }} onClick={nextImage}>
                                <ChevronRight size={32} color="#fff" />
                            </button>
                        </>
                    )}
                    <img src={imageModal.images[imageModal.currentIndex]} alt="확대 이미지" style={styles.expandedImage} onClick={(e) => e.stopPropagation()} />
                    {imageModal.images.length > 1 && (
                        <div style={styles.imageCounter}>
                            {imageModal.currentIndex + 1} / {imageModal.images.length}
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                table tbody tr { cursor: pointer; }
                table tbody tr:hover { background-color: #f8fafc !important; }
                table tbody tr.unread-row { background-color: #fffde7 !important; }
                table tbody tr.unread-row:hover { background-color: #fff9c4 !important; }
                @media print {
                    table tbody tr, 
                    table tbody tr.unread-row,
                    table tbody tr.unread-row:hover {
                        background-color: #ffffff !important;
                    }
                }
                @keyframes slideUp {
                    from { transform: translateY(30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes notifSlide {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' },

    scrollView: { flex: 1, overflowY: 'auto' },
    contentWrapper: { maxWidth: 1200, margin: '0 auto', padding: 20, width: '100%', boxSizing: 'border-box' },

    filterCard: { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 },
    filterRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    filterLabel: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 50 },
    filterInput: { flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#334155', outline: 'none', paddingRight: 30 },
    tabGroup: { display: 'flex', gap: 4 },
    tabBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' },
    tabBtnActive: { background: '#6366f1', color: 'white', borderColor: '#6366f1' },
    filterSelect: { padding: '7px 12px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
    newOrderBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", backgroundColor: "#6366f1", border: "none", borderRadius: 8, fontSize: 13, fontWeight: "600", color: "#fff", cursor: "pointer" },
    editModeBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: "600", color: "#64748b", cursor: "pointer" },
    editModeBtnActive: { backgroundColor: "#6366f1", borderColor: "#6366f1", color: "#fff" },
    resetBtn: { padding: '8px 12px', backgroundColor: '#fef2f2', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: '600', color: '#ef4444', cursor: 'pointer' },

    tableContainer: { backgroundColor: '#ffffff', borderRadius: 12, overflowX: 'auto', overflowY: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },   // 좁은 창은 옆으로 스크롤(09-02)
    table: { width: '100%', minWidth: 1040, borderCollapse: 'collapse', tableLayout: 'fixed' },
    th: { padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: '600', color: '#64748b', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '10px 12px', fontSize: 13, color: '#374151', verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: '600' },
    patientName: { fontWeight: '600', color: '#0f172a', fontSize: 13 },
    partnerText: { color: '#6366f1', fontSize: 13 },
    dateText: { color: '#64748b', fontSize: 13 },
    dueDateText: { color: '#f59e0b', fontSize: 13, fontWeight: '500' },

    actionBtn: { width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eff6ff', color: '#1e40af', border: 'none', borderRadius: 6, cursor: 'pointer' },
    deleteBtn: { backgroundColor: '#fef2f2', color: '#991b1b' },

    imagePreviewCell: { display: 'flex', alignItems: 'center', gap: 4 },
    thumbImage: { width: 50, height: 50, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #e2e8f0' },
    imageCount: { fontSize: 11, fontWeight: '600', color: '#64748b', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 4 },
    noImage: { color: '#cbd5e1', fontSize: 12 },

    // PDF/인쇄 상태 스타일
    statusContainer: { display: 'flex', gap: 6 },
    statusPdf: { fontSize: 11, fontWeight: '600', color: '#dc2626', backgroundColor: '#fef2f2', padding: '3px 8px', borderRadius: 4 },
    statusPrint: { fontSize: 11, fontWeight: '600', color: '#2563eb', backgroundColor: '#eff6ff', padding: '3px 8px', borderRadius: 4 },
    noStatus: { color: '#cbd5e1', fontSize: 12 },

    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', backgroundColor: '#ffffff', borderRadius: 12 },
    emptyText: { margin: '12px 0', fontSize: 14, color: '#9ca3af' },
    emptyCreateBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: '600', cursor: 'pointer' },

    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 },
    spinner: { width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    loadingText: { marginTop: 12, color: '#64748b', fontSize: 13 },
    errorContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 },
    errorText: { marginTop: 12, color: '#ef4444', fontSize: 13 },
    retryButton: { marginTop: 12, padding: '10px 20px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: '600', cursor: 'pointer' },

    imageModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalCloseBtn: { position: 'absolute', top: 16, right: 16, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', zIndex: 1001 },
    modalNavBtn: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1001 },
    expandedImage: { maxWidth: '85%', maxHeight: '85%', objectFit: 'contain', borderRadius: 8 },
    imageCounter: { position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 14, fontWeight: '600', backgroundColor: 'rgba(0,0,0,0.5)', padding: '6px 16px', borderRadius: 20 },

    // 푸시 알림 모달 스타일
};

export default OrderList;